<template>
  <transition name="about-modal">
    <div v-if="open" class="about-modal">
      <button
        class="about-modal__backdrop"
        type="button"
        aria-label="Dialog schließen"
        @click="emit('close')"
      ></button>

      <section
        ref="panelRef"
        class="about-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="about-modal-title"
        tabindex="-1"
      >
        <div class="about-modal__top">
          <div>
            <p class="section-kicker">Über mich</p>
            <h2 id="about-modal-title">Krilin Schmid</h2>
            <p>
              Informatiker Betriebsinformatik EFZ und Foto- &amp; Videograf mit Leidenschaft für echte Momente,
              klare Bilder und Geschichten mit Charakter.
            </p>
          </div>

          <button
            class="about-modal__close"
            type="button"
            aria-label="Dialog schließen"
            @click="emit('close')"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <div class="about-tabs" role="tablist" aria-label="Lebenslauf-Kategorie">
          <button
            type="button"
            role="tab"
            :aria-selected="selectedCategory === 'business'"
            :class="{ active: selectedCategory === 'business' }"
            @click="selectedCategory = 'business'"
          >
            Beruflich
          </button>

          <button
            type="button"
            role="tab"
            :aria-selected="selectedCategory === 'private'"
            :class="{ active: selectedCategory === 'private' }"
            @click="selectedCategory = 'private'"
          >
            Privat &amp; Hobbys
          </button>
        </div>

        <div class="about-timeline" role="tabpanel">
          <article
            v-for="item in filteredTimeline"
            :key="getItemId(item)"
            class="about-timeline__item"
          >
            <div class="about-timeline__date">
              {{ item.date }}
            </div>

            <div class="about-timeline__card">
              <p v-if="item.label" class="about-timeline__label">
                {{ item.label }}
              </p>

              <h3>{{ item.title }}</h3>
              <p>{{ item.description }}</p>

              <button
                v-if="item.achievements?.length || item.subItems?.length"
                type="button"
                class="about-timeline__toggle"
                :aria-expanded="Boolean(expandedItems[getItemId(item)])"
                @click="toggleItem(item)"
              >
                {{ expandedItems[getItemId(item)] ? 'Weniger anzeigen' : 'Mehr anzeigen' }}
                <span aria-hidden="true">{{ expandedItems[getItemId(item)] ? '−' : '+' }}</span>
              </button>

              <transition name="about-details">
                <div
                  v-if="expandedItems[getItemId(item)]"
                  class="about-timeline__details"
                >
                  <ul v-if="item.achievements?.length">
                    <li v-for="achievement in item.achievements" :key="achievement">
                      {{ achievement }}
                    </li>
                  </ul>

                  <div
                    v-for="sub in item.subItems"
                    :key="sub.title"
                    class="about-timeline__sub"
                  >
                    <h4>{{ sub.title }}</h4>
                    <ul>
                      <li v-for="achievement in sub.achievements" :key="achievement">
                        {{ achievement }}
                      </li>
                    </ul>
                  </div>
                </div>
              </transition>
            </div>
          </article>
        </div>
      </section>
    </div>
  </transition>
</template>

<script setup>
import {
  computed,
  defineEmits,
  defineProps,
  nextTick,
  onUnmounted,
  ref,
  watch,
} from 'vue'

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close'])

const panelRef = ref(null)
const selectedCategory = ref('business')
const expandedItems = ref({})
let previousBodyOverflow = ''

function getItemId(item) {
  return `${item.category}-${item.date}-${item.title}`
}

function toggleItem(item) {
  const id = getItemId(item)

  expandedItems.value = {
    ...expandedItems.value,
    [id]: !expandedItems.value[id],
  }
}

function handleEscape(event) {
  if (event.key === 'Escape') {
    emit('close')
  }
}

const timeline = [

    {
    category: 'business',
    date: 'März 2026 - heute',
    title: 'Marketingassistent @ Interhockey',
    description:
        'Nach Abschluss meiner obligatorischen Dienstpflicht hat mich mein Weg zurück zur Interhockey geführt. Gleiche Firma, neue Aufgaben mit Fokus auf Markenauftritt, Webshop, Content und Design.',
    achievements: [
        'Corporate Identity: Weiterentwicklung von CI/CD-Richtlinien für einen konsistenten Markenauftritt über alle Kanäle.',
        'Webshop-Management: Pflege, Optimierung, Artikelerfassung und Strukturierung von Produktdaten.',
        'Content-Produktion: Erstellung und Bearbeitung von Produktbildern inklusive eigener Fotografie bei fehlendem Bildmaterial.',
        'Marketingkampagnen: Planung, Konzeption und Umsetzung von Newsletter- und Social-Media-Kampagnen.',
        'Grafikdesign: Erstellung von Werbemitteln für Print und Digital im Einklang mit der Markenidentität.',
        'Produktdesign: Gestaltung von Textilkollektionen und Merchandisingprodukten für Vereine.',
        'Publikationen: Gestaltung, Erstellung und Aktualisierung von Produktkatalogen und bestehenden Unterlagen.',
    ],
    },
  {
    category: 'business',
    date: 'Juli 2025 - März 2026',
    title: 'Zivildienst IT in der Stiftung Lerchenbühl',
    description:
      'Direkt im Anschluss an meinen ersten Einsatz habe ich einen weiteren Zivildienst-Einsatz gemacht, um danach wieder voll auf meinen beruflichen Weg fokussieren zu können.',
    achievements: [
      'Unterstützung der Lehrperson im Klassenzimmer und in der Berufsvorbereitung',
      '1st und 2nd Level Support der Mitarbeiter/innen',
      'Projektübernahme von diversen IT-Projekten',
    ],
  },
  {
    category: 'business',
    date: 'Januar 2025 - Juni 2025',
    title: 'Zivildienst an den Schulen der Gemeinde Lützelflüh',
    description:
      'Als Zivildienstleistender unterstützte ich die Schule im Alltag, in der Tagesstruktur und bei administrativen Aufgaben.',
    achievements: [
      'Unterstützung der Lehrperson im Klassenzimmer',
      'Begleitperson in der Tagesstruktur',
      'Administrative Aufgaben in der Schule',
    ],
  },
  {
    category: 'business',
    date: 'August 2023 - Dezember 2024',
    title: 'Marketingassistent @ Interhockey',
    description:
      'Zuständig für Merchandising-Management, Webseiten, Webshops, Content und digitale Kommunikation rund um Sportclubs und Unternehmen.',
    achievements: [
      'Sortimentmanagement für Merchandising des SC Bern und HC Ajoie',
      'Leitung von Merchandisingprojekten für Sportclubs und Unternehmen',
      'Pflege und Aktualisierung von Webseiten und Webshops',
      'Erstellung von Anzeigen, Newslettern und Social-Media-Beiträgen',
      'Gestaltung von Produktlayouts',
    ],
  },
  {
    category: 'business',
    date: 'Januar 2023 - Juli 2023',
    title: 'Auftragsbearbeitung @ Interhockey',
    description:
      'Mitarbeit in der Auftragsbearbeitung, Kundenpflege, Webshop-Support und interner IT-Unterstützung.',
    achievements: [
      'Kontrolle der Kassenabschlüsse',
      'Bearbeitung von Kundenbestellungen',
      'Erfassung und Wartung von Kundenkonten',
      'Unterstützung bei Webshop- und IT-Themen',
    ],
  },
  {
    category: 'business',
    date: 'August 2022 - Dezember 2022',
    title: 'Berufliche Auszeit',
    description:
      'Reise nach Hawaii und New York — eine prägende Zeit mit neuen Eindrücken, viel Inspiration und persönlichem Wachstum.',
    achievements: [],
  },
  {
    category: 'business',
    date: 'August 2018 - Juli 2022',
    title: 'Lehre Informatiker Betriebsinformatik EFZ @ Swisscom',
    label: 'Swisscom Lehre',
    description:
      'Während meiner Ausbildung durchlief ich verschiedene technische und kreative Abteilungen bei Swisscom.',
    subItems: [
      {
        title: 'Fullstack Developer @ AppsTeam',
        achievements: [
          'Frontend- und Backend-Weiterentwicklung',
          'Datenverwaltung mit TypeOrm/MySQL',
          'Onboarding von Telekommunikationsservices',
        ],
      },
      {
        title: 'Mac Engineering Service @ Swisscom',
        achievements: [
          'Support von Mac-Benutzern',
          'Automatisierung mit Bash',
          'Weiterentwicklung von Managed Mac Services',
        ],
      },
      {
        title: 'Frontend Developer @ Traffy',
        achievements: [
          'Frontendentwicklung mit Vue.js',
          'Erste Erfahrungen mit Git',
          'SCRUM kennengelernt',
        ],
      },
    ],
  },
  {
    category: 'private',
    date: 'Seit 2022',
    title: 'Foto- & Videograf',
    description:
      'In meiner Freizeit widme ich mich mit Leidenschaft der Fotografie und Videografie — von Sport über Street bis zu Portraits, Events und Automotive.',
    achievements: [
      'Aufbau einer professionellen Kameraausrüstung',
      'Erfahrung mit Adobe Lightroom & DaVinci Resolve',
      'Präsentation meiner Arbeiten über Instagram und eigene Website',
      'Sportfotografie bei Heimspielen und Events',
    ],
  },
  {
    category: 'private',
    date: 'Seit 2017',
    title: 'Aktivmitglied im Turnverein Bärau',
    description:
      'Vielseitig sportlich aktiv mit Fokus auf Leichtathletik, Unihockey und Vereinsleben.',
    achievements: [
      'Co-Leitung des Unihockeyteams',
      'Trainingsorganisation und Mitgliedergewinnung',
      'Pflege der Vereinswebseite und des Instagram-Kanals',
    ],
  },
  {
    category: 'private',
    date: 'Seit 2010',
    title: 'Leidenschaftlicher Eishockeyspieler',
    description:
      'Eishockey begleitet mich seit meiner Kindheit — vom Nachwuchsspieler bis zum heutigen Plauschhockey.',
    achievements: [
      'Juniorenzeit bei den SCL Young Tigers und HC Huskys Oberlangenegg',
      'Heute aktiv als Plauschhockeyspieler',
    ],
  },
  {
    category: 'private',
    date: 'Seit Juni 2023',
    title: 'Jugendsportleiter 6.–9. Klasse',
    description:
      'Ich unterstütze Jugendliche im Sport und begleite sie bei Trainings, Wettkämpfen und Vereinsanlässen.',
    achievements: [
      'Leitung und Mitgestaltung von Trainings',
      'Betreuung bei Wettkämpfen',
      'Förderung von Teamgeist und Bewegung',
    ],
  },

]

const filteredTimeline = computed(() => {
  return timeline.filter((item) => item.category === selectedCategory.value)
})

watch(selectedCategory, () => {
  expandedItems.value = {}
})

watch(
  () => props.open,
  async (isOpen) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return
    }

    if (isOpen) {
      previousBodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      window.addEventListener('keydown', handleEscape)

      await nextTick()
      panelRef.value?.focus()
      return
    }

    document.body.style.overflow = previousBodyOverflow
    window.removeEventListener('keydown', handleEscape)
  },
  { immediate: true }
)

onUnmounted(() => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }

  document.body.style.overflow = previousBodyOverflow
  window.removeEventListener('keydown', handleEscape)
})
</script>

<style scoped>
/* LOGO-BLAU: #373D6B · RGB 55, 61, 107 */
.about-modal {
  --background: #f7f9fd;
  --surface: #ffffff;
  --surface-soft: #f0f1f7;
  --navy: #171a31;
  --navy-soft: #242a4c;
  --blue: #373d6b;
  --blue-dark: #292e52;
  --blue-light: #626c9f;
  --text: #171a31;
  --text-soft: #4d5d75;
  --text-muted: #77869c;
  --line: rgba(41, 46, 82, 0.12);
  --line-strong: rgba(55, 61, 107, 0.28);
  --shadow-soft: 0 20px 60px rgba(41, 46, 82, 0.1);

  position: fixed;
  inset: 0;
  z-index: 500;
  display: grid;
  place-items: center;
  padding: 24px;
  color: var(--text);
  font-family: 'Halenoir', Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.about-modal__backdrop {
  position: absolute;
  inset: 0;
  border: 0;
  background:
    radial-gradient(circle at 18% 4%, rgba(55, 61, 107, 0.24), transparent 34rem),
    rgba(23, 26, 49, 0.72);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  cursor: pointer;
}

.about-modal__panel {
  position: relative;
  z-index: 1;
  width: min(100%, 1120px);
  max-height: min(90dvh, 980px);
  overflow: auto;
  overscroll-behavior: contain;
  border: 1px solid rgba(55, 61, 107, 0.16);
  border-radius: 30px;
  background:
    radial-gradient(circle at 4% 0%, rgba(55, 61, 107, 0.1), transparent 27rem),
    linear-gradient(180deg, #ffffff 0%, #fbfcff 58%, #f7f7fa 100%);
  box-shadow:
    0 38px 120px rgba(23, 26, 49, 0.34),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  padding: clamp(28px, 4vw, 48px);
  outline: none;
  scrollbar-color: rgba(55, 61, 107, 0.42) transparent;
  scrollbar-width: thin;
}

.about-modal__panel::-webkit-scrollbar {
  width: 9px;
}

.about-modal__panel::-webkit-scrollbar-track {
  background: transparent;
}

.about-modal__panel::-webkit-scrollbar-thumb {
  border: 3px solid transparent;
  border-radius: 999px;
  background: rgba(55, 61, 107, 0.42);
  background-clip: padding-box;
}

.about-modal__top {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 32px;
  padding-bottom: 34px;
  border-bottom: 1px solid var(--line);
}

.section-kicker {
  margin: 0 0 16px;
  color: var(--blue);
  font-size: 0.74rem;
  font-weight: 900;
  letter-spacing: 0.25em;
  text-transform: uppercase;
}

.about-modal__top h2 {
  margin: 0;
  color: var(--navy);
  font-size: clamp(3.4rem, 7vw, 7.2rem);
  font-weight: 900;
  letter-spacing: -0.07em;
  line-height: 0.9;
}

.about-modal__top p:not(.section-kicker) {
  max-width: 760px;
  margin: 24px 0 0;
  color: var(--text-soft);
  font-size: 1.06rem;
  line-height: 1.76;
}

.about-modal__close {
  position: sticky;
  top: 0;
  display: grid;
  width: 48px;
  height: 48px;
  place-items: center;
  flex: 0 0 auto;
  border: 1px solid var(--line-strong);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.86);
  color: var(--navy);
  box-shadow: 0 12px 30px rgba(41, 46, 82, 0.1);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  cursor: pointer;
  font: inherit;
  font-size: 1.9rem;
  line-height: 1;
  transition:
    transform 180ms ease,
    border-color 180ms ease,
    background 180ms ease,
    color 180ms ease,
    box-shadow 180ms ease;
}

.about-modal__close:hover {
  transform: rotate(7deg) scale(1.04);
  border-color: transparent;
  background: linear-gradient(135deg, var(--blue-light), var(--blue) 58%, var(--blue-dark));
  color: #ffffff;
  box-shadow: 0 16px 34px rgba(55, 61, 107, 0.26);
}

.about-tabs {
  position: sticky;
  top: 14px;
  z-index: 4;
  display: inline-flex;
  gap: 5px;
  margin-top: 30px;
  padding: 5px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.86);
  box-shadow: 0 12px 34px rgba(41, 46, 82, 0.1);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.about-tabs button {
  min-height: 44px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--text-soft);
  cursor: pointer;
  font: inherit;
  font-size: 0.9rem;
  font-weight: 900;
  padding: 0 20px;
  transition:
    transform 180ms ease,
    background 180ms ease,
    color 180ms ease,
    box-shadow 180ms ease;
}

.about-tabs button:hover {
  transform: translateY(-1px);
  background: var(--surface-soft);
  color: var(--blue-dark);
}

.about-tabs button.active {
  background: linear-gradient(135deg, #626c9f, var(--blue) 58%, var(--blue-dark));
  color: #ffffff;
  box-shadow:
    0 10px 24px rgba(55, 61, 107, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.about-modal button:focus-visible {
  outline: 3px solid rgba(55, 61, 107, 0.3);
  outline-offset: 3px;
}

.about-timeline {
  position: relative;
  display: grid;
  gap: 18px;
  margin-top: 32px;
}

.about-timeline::before {
  content: '';
  position: absolute;
  top: 26px;
  bottom: 26px;
  left: 196px;
  width: 1px;
  background: linear-gradient(to bottom, transparent, rgba(55, 61, 107, 0.24) 6%, rgba(55, 61, 107, 0.24) 94%, transparent);
}

.about-timeline__item {
  position: relative;
  display: grid;
  grid-template-columns: 172px minmax(0, 1fr);
  gap: 46px;
}

.about-timeline__item::before {
  content: '';
  position: absolute;
  z-index: 2;
  top: 29px;
  left: 191px;
  width: 11px;
  height: 11px;
  border: 4px solid #ffffff;
  border-radius: 999px;
  background: var(--blue);
  box-shadow: 0 0 0 1px rgba(55, 61, 107, 0.24);
}

.about-timeline__date {
  padding-top: 25px;
  color: var(--blue-dark);
  font-size: 0.76rem;
  font-weight: 900;
  letter-spacing: 0.11em;
  line-height: 1.55;
  text-align: right;
  text-transform: uppercase;
}

.about-timeline__card {
  position: relative;
  padding: 27px 28px;
  border: 1px solid var(--line);
  border-radius: 23px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 14px 40px rgba(41, 46, 82, 0.065);
  transition:
    transform 220ms ease,
    border-color 220ms ease,
    box-shadow 220ms ease;
}

.about-timeline__card:hover {
  transform: translateY(-3px);
  border-color: var(--line-strong);
  box-shadow: 0 22px 54px rgba(41, 46, 82, 0.11);
}

.about-timeline__label {
  display: inline-flex;
  margin: 0 0 15px;
  padding: 8px 12px;
  border: 1px solid rgba(55, 61, 107, 0.14);
  border-radius: 999px;
  background: var(--surface-soft);
  color: var(--blue-dark);
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.about-timeline__card h3 {
  margin: 0;
  color: var(--navy);
  font-size: clamp(1.35rem, 2vw, 2rem);
  font-weight: 900;
  letter-spacing: -0.04em;
  line-height: 1.08;
}

.about-timeline__card > p:not(.about-timeline__label) {
  margin: 14px 0 0;
  color: var(--text-soft);
  font-size: 0.98rem;
  line-height: 1.72;
}

.about-timeline__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 42px;
  margin-top: 20px;
  padding: 0 16px;
  border: 1px solid var(--line-strong);
  border-radius: 999px;
  background: #ffffff;
  color: var(--blue-dark);
  cursor: pointer;
  font: inherit;
  font-size: 0.86rem;
  font-weight: 900;
  transition:
    transform 180ms ease,
    border-color 180ms ease,
    background 180ms ease,
    color 180ms ease,
    box-shadow 180ms ease;
}

.about-timeline__toggle span {
  display: grid;
  width: 20px;
  height: 20px;
  place-items: center;
  border-radius: 50%;
  background: var(--surface-soft);
  font-size: 1rem;
  line-height: 1;
}

.about-timeline__toggle:hover {
  transform: translateY(-1px);
  border-color: transparent;
  background: var(--blue);
  color: #ffffff;
  box-shadow: 0 12px 28px rgba(55, 61, 107, 0.22);
}

.about-timeline__toggle:hover span {
  background: rgba(255, 255, 255, 0.16);
}

.about-timeline__details {
  margin-top: 22px;
  padding: 22px;
  border: 1px solid rgba(55, 61, 107, 0.11);
  border-radius: 18px;
  background: linear-gradient(145deg, rgba(240, 241, 247, 0.82), rgba(250, 250, 253, 0.96));
}

.about-timeline__details ul {
  display: grid;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.about-timeline__details li {
  position: relative;
  padding-left: 22px;
  color: var(--text-soft);
  font-size: 0.94rem;
  line-height: 1.62;
}

.about-timeline__details li::before {
  content: '';
  position: absolute;
  top: 0.68em;
  left: 0;
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: var(--blue);
  box-shadow: 0 0 0 4px rgba(55, 61, 107, 0.09);
}

.about-timeline__sub {
  margin-top: 22px;
  padding: 2px 0 2px 19px;
  border-left: 2px solid rgba(55, 61, 107, 0.28);
}

.about-timeline__sub h4 {
  margin: 0 0 13px;
  color: var(--navy);
  font-size: 1.06rem;
  font-weight: 900;
}

.about-modal-enter-active,
.about-modal-leave-active {
  transition: opacity 240ms ease;
}

.about-modal-enter-active .about-modal__panel,
.about-modal-leave-active .about-modal__panel {
  transition:
    opacity 280ms ease,
    transform 280ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.about-modal-enter-from,
.about-modal-leave-to {
  opacity: 0;
}

.about-modal-enter-from .about-modal__panel,
.about-modal-leave-to .about-modal__panel {
  opacity: 0;
  transform: translateY(18px) scale(0.985);
}

.about-details-enter-active,
.about-details-leave-active {
  transition:
    opacity 210ms ease,
    transform 210ms ease;
}

.about-details-enter-from,
.about-details-leave-to {
  opacity: 0;
  transform: translateY(-7px);
}

@media (max-width: 820px) {
  .about-modal {
    align-items: end;
    padding: 10px;
  }

  .about-modal__panel {
    width: 100%;
    max-height: 94dvh;
    padding: 26px 20px 34px;
    border-radius: 26px 26px 18px 18px;
  }

  .about-modal__top {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 18px;
    padding-bottom: 28px;
  }

  .about-modal__top h2 {
    font-size: clamp(3rem, 15vw, 5rem);
  }

  .about-modal__top p:not(.section-kicker) {
    font-size: 0.98rem;
  }

  .about-tabs {
    top: 10px;
    width: 100%;
  }

  .about-tabs button {
    flex: 1;
    padding-inline: 12px;
  }

  .about-timeline {
    gap: 16px;
  }

  .about-timeline::before,
  .about-timeline__item::before {
    display: none;
  }

  .about-timeline__item {
    grid-template-columns: 1fr;
    gap: 9px;
  }

  .about-timeline__date {
    padding: 0 4px;
    text-align: left;
  }

  .about-timeline__card {
    padding: 23px 21px;
  }
}

@media (max-width: 480px) {
  .about-modal__top h2 {
    font-size: clamp(2.8rem, 16vw, 4.2rem);
  }

  .about-modal__close {
    width: 44px;
    height: 44px;
  }

  .about-tabs button {
    min-height: 42px;
    font-size: 0.82rem;
  }

  .about-timeline__details {
    padding: 18px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .about-modal *,
  .about-modal *::before,
  .about-modal *::after {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
  }
}
</style>
